import { useState } from 'react';
import { useStore } from 'zustand';

import { SearchResultTab } from './search-result-tab.component';

import { searchHealthResources } from '@/app/(main)/_components/search-modal/_services/search-health-resources.service';
import { AllSearchResults, SearchHealthResourcesResponse, SearchResult, SearchType } from '@/app/(main)/_components/search-modal/search-modal.types';
import { searchHealthResourcesByType } from '@/app/(main)/_components/search-modal/_services/search-health-resources-by-type.service';
import { Button } from '@/components/ui/button/button.component';
import { setFetchedResultsByType, useSearchState } from '@/store/search/search.store';
import { SelectedProcedures } from '@/app/(main)/(pages)/_components/search/search.component';
import { OverridedUserLocation } from '@/utils/formatters/city/format-place-from-slug.const';
import { getLogger } from '@/logger/get-logger';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

interface SearchResultsProps {
  results: SearchHealthResourcesResponse;
  searchType: SearchType;
  searchTerm: string;
  selectLaboratoryProcedure?: (procedure: SelectedProcedures) => void;
  setOpen: (open: boolean) => void;
  locationOverride?: OverridedUserLocation;
}

export const SearchResults = ({
  results,
  searchType,
  searchTerm,
  setOpen,
  selectLaboratoryProcedure,
  locationOverride,
}: SearchResultsProps) => {
  const fetchedResults = useStore(useSearchState, (state) => state?.fetchedResults);

  const chooseInitialDisplayedResults = () => {
    if (fetchedResults) {
      return fetchedResults[searchType as keyof AllSearchResults].results;
    }

    return results.results;
  };

  const chooseInitialPage = () => {
    if (fetchedResults) {
      return Math.ceil(fetchedResults[searchType as keyof AllSearchResults].results.length / 10);
    }

    return 1;
  };

  const [displayedResults, setDisplayedResults] = useState<SearchResult[]>(chooseInitialDisplayedResults());
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(chooseInitialPage());
  const [hasMore, setHasMore] = useState(displayedResults.length < results.total);

  const loadMoreResults = async () => {
    if (isLoading || !hasMore) {
      return;
    }

    setIsLoading(true);
    try {
      const limit = 10;
      let response: SearchHealthResourcesResponse;

      if (searchType === SearchType.ALL) {
        response = await searchHealthResources(searchTerm, page + 1, limit);
      } else {
        response = await searchHealthResourcesByType(searchTerm, searchType, page + 1, limit);
      }

      setDisplayedResults((prev) => [...prev, ...response.results]);
      setFetchedResultsByType(searchType, [...displayedResults, ...response.results], response.total);
      setPage((prevPage) => prevPage + 1);
      setHasMore(displayedResults.length + response.results.length < response.total);
    } catch (error) {
      const logger = getLogger('SearchModal - SearchResults', {});
      logger.error('Error loading more results', { searchType, searchTerm, page, error });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      {displayedResults.map((result: SearchResult, index: number) => (
        <div key={`${result.id}${result.displayName}${index}`} className="hover:bg-gray-50 transition-colors">
          <SearchResultTab
            {...result}
            locationOverride={locationOverride}
            searchTerm={searchTerm}
            setOpen={setOpen}
            selectLaboratoryProcedure={selectLaboratoryProcedure}
          />
          <hr />
        </div>
      ))}

      {hasMore && (
        <Button
          className="md:w-fit md:mx-auto mt-6"
          variant="secondary"
          onClick={loadMoreResults}
          disabled={isLoading}
          isLoading={isLoading}
          data-analytics-event={AnalyticsEnum.CLICK_SHOW_MORE_RESULTS_PROCEDURES_MODAL}
        >
          Carregar mais
        </Button>
      )}
    </div>
  );
};
